import { AuthApiError, apiBaseUrl } from '../account/authApi';

/**
 * Public-profile + username-claim endpoints.
 *
 * Profile lookups (`/u/{username}`) are public. The claim endpoint
 * (`PATCH /auth/me/username`) sits on the auth router — we proxy it
 * here so the username feature has one frontend module.
 */

export interface ProfileSubmission {
  id: string;
  project_name: string;
  project_kind: 'image' | 'audio' | 'pose';
  description: string | null;
  class_labels: string[];
  submitted_at: string;
}

export interface ProfilePublishedApp {
  id: string;
  title: string;
  slug: string;
  share_url: string;
  view_count: number;
  version: number;
  published_at: string;
}

export interface ProfilePayload {
  username: string;
  display_name: string | null;
  submissions: ProfileSubmission[];
  published_apps: ProfilePublishedApp[];
  total_submissions: number;
  total_published_apps: number;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    /* empty / non-JSON */
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function getPublicProfile(username: string): Promise<ProfilePayload> {
  return request<ProfilePayload>(`/u/${encodeURIComponent(username)}`);
}

/** PATCH /auth/me/username — claim or change the signed-in user's
 *  username. Throws AuthApiError(409) if taken, AuthApiError(422)
 *  with a kid-readable detail if the shape is invalid. */
export async function claimUsername(username: string): Promise<{ username: string }> {
  const payload = await request<{ username: string }>('/auth/me/username', {
    method: 'PATCH',
    body: JSON.stringify({ username }),
  });
  return payload;
}
